import { useSyncExternalStore } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  duration?: number;
}

let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 4000;

function emit() {
  listeners.forEach((l) => l());
}

export function dismissToast(id: string) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

let seq = 0;

export function toast(input: Omit<Toast, 'id'>) {
  const id = `toast-${++seq}`;
  const duration = input.duration ?? (input.variant === 'error' ? 0 : DEFAULT_DURATION);

  toasts = [...toasts, { ...input, id, duration }].slice(-MAX_VISIBLE);
  emit();

  if (duration > 0) timers.set(id, setTimeout(() => dismissToast(id), duration));
  return id;
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => toasts,
    () => toasts,
  );
}

export function useToast() {
  return { toast, dismiss: dismissToast };
}
